import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { performRequestMassAction } from '../../../../api'
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../constants/common'
import {
  useMassActions,
  useNotification,
  useRefetchCounters,
} from '../../../../hooks'
import { getItemsIds, getMassActionIdParam } from '../../../../utils/common'
import { getMassActionsSuccessItemsLength } from '../../../../utils/massActions'
import {
  FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME,
  getFormikEntityActionConfigFieldsInitialValuesPayload,
} from '../../../Formik'
import { getRelatedRequestsLimitErrorMeta } from '../../AttachServiceRequestsModal'
import { REQUEST_MASS_ACTION_NAMES } from '../../Request.constants.actions'
import { getRequestMassActionFilters } from '../../Request.utils.massActionFilters'
import {
  getAsyncNotificationText,
  getNotificationTitle,
} from '../../Request.utils.notifications'

export function useRequestTableMassActionRequest({
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseModal,
  onReload,
}) {
  const refetchCounters = useRefetchCounters()

  const { t } = useTranslation()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText,
    getNotificationTitle,
    onSuccessHandler: onReload,
  })

  function runMassAction({
    action,
    countOfItems,
    performAction,
    onSucceed = EMPTY_FUNCTION,
  }) {
    function handleSucceed(data) {
      const successItemsCount = getMassActionsSuccessItemsLength(data)

      if (successItemsCount > 0) {
        onSucceed(data, successItemsCount)
      }
    }

    onCloseModal()

    setPerformingMassAction(true)

    performAction()
      .then(({ data }) => {
        onCleanSelection()

        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            pathname: location.pathname,
            sub,
            task: asyncTask,
            totalCount: countOfItems,
            onFinish: handleSucceed,
          })

          return
        }

        onReload()

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: countOfItems,
        })

        handleSucceed(data)

        refetchCounters()
      })
      .catch(error => {
        onCloseModal()

        const limitErrorMeta = getRelatedRequestsLimitErrorMeta(error)

        handleAddErrorNotification(
          limitErrorMeta
            ? {
                title: t(
                  'Modal.AttachServiceRequestsLimitExceededKey',
                  limitErrorMeta
                ),
              }
            : 'Notification.RequestMassActionErrorKey'
        )
      })
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  function massAction({
    action,
    actionType,
    ids,
    value = EMPTY_OBJECT,
    onSucceed,
  }) {
    const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })

    const params = {
      ...getFormikEntityActionConfigFieldsInitialValuesPayload(actionType),
      action: REQUEST_MASS_ACTION_NAMES[action],
      all: selectAllPages,
      filters: getRequestMassActionFilters({
        excludeIds,
        filterValues,
        ids: id,
        selectAllPages,
        sub,
      }),
      payload: value,
    }

    runMassAction({
      action,
      countOfItems: selectAllPages ? pagination.count : id.length,
      performAction: () => performRequestMassAction(params),
      onSucceed,
    })
  }

  async function handleAttachRequests({ action, ids, selected, status }) {
    massAction({
      action,
      ids,
      value: {
        ids: getItemsIds(selected),
        ...(status ? { restore_archived_with_status: status } : null),
      },
    })
  }

  function getHandleSubmitActionWithType({
    action,
    getOnSucceed = EMPTY_FUNCTION,
    id,
  }) {
    return ({ payload, values }) => {
      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        actionType: values[FORMIK_ENTITY_ACTION_CONFIG_FIELDS_TYPE_SELECT_NAME],
        value: payload,
        onSucceed: getOnSucceed(),
      })
    }
  }

  return {
    getHandleSubmitActionWithType,
    handleAttachRequests,
    isPerformingMassAction,
    massAction,
    runMassAction,
  }
}
